import React from 'react'
import { useTranslation } from 'react-i18next'
import { Lock, Shield } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { CertificatesModuleConfig, ModuleFieldComponentProps } from '@/types/space-wizard'

/**
 * "Quem emite" for a certificates space (F-42 §4.3): the team or admins only,
 * admins by default. There is no "anyone": a certificate is something the
 * community attests, and the verification page would call valid what the
 * community never gave.
 *
 * It stands in for the generic "who can post", which this module hides.
 */
export const CertificatesModuleFields: React.FC<ModuleFieldComponentProps<CertificatesModuleConfig>> = ({
  config,
  onChange,
  disabled = false
}) => {
  const { t } = useTranslation('common')
  const value = config.whoCanIssue || 'admins'

  const options = [
    { value: 'admins' as const, icon: Lock, label: t('modules.settings.certificates.admins'), hint: t('modules.settings.certificates.adminsHint') },
    { value: 'staff' as const, icon: Shield, label: t('modules.settings.certificates.staff'), hint: t('modules.settings.certificates.staffHint') }
  ]

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">{t('modules.settings.certificates.whoCanIssue')}</label>
      <div className="flex flex-wrap gap-2">
        {options.map(({ value: optionValue, icon: Icon, label, hint }) => (
          <button
            key={optionValue}
            type="button"
            disabled={disabled}
            title={hint}
            onClick={() => onChange({ ...config, whoCanIssue: optionValue })}
            className={cn(
              'flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition-colors',
              value === optionValue ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            <Icon className="h-4 w-4 flex-shrink-0" />
            <span>{label}</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500">
        {value === 'staff' ? t('modules.settings.certificates.staffHint') : t('modules.settings.certificates.adminsHint')}
      </p>
      <p className="text-xs text-gray-500">{t('modules.settings.certificates.revokeNote')}</p>
    </div>
  )
}
